import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Clock, FileText, Download, Trash2, Sparkles, ArrowLeft, Eye, EyeOff
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HistoryDetailView({ result, onBack }) {
  const r = result;
  const score = r.ats_score || {};
  const overall = score.overall_score || 0;
  const matchedKeywords = score.matched_keywords || [];
  const [boldOn, setBoldOn] = useState(r.bold_keywords_on !== false);
  const getScoreColor = (val) => val >= 75 ? "#10B981" : val >= 50 ? "#F59E0B" : "#EF4444";
  const getScoreClass = (val) => val >= 75 ? "high" : val >= 50 ? "medium" : "low";

  const handleToggleBold = async () => {
    const newVal = !boldOn;
    setBoldOn(newVal);
    try {
      await axios.patch(`${API}/optimizations/${r.id}/bold-preference`, { bold_keywords_on: newVal });
    } catch { /* ignore */ }
  };

  const highlightText = (text, keywords) => {
    if (!keywords || keywords.length === 0) return text;
    const sorted = [...keywords].sort((a, b) => b.length - a.length);
    const parts_arr = sorted.map((k) => {
      const escaped = k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const startsAlnum = /^\w/.test(k);
      const endsAlnum = /\w$/.test(k);
      if (startsAlnum && endsAlnum) return `\\b${escaped}\\b`;
      if (startsAlnum) return `\\b${escaped}(?!\\w)`;
      if (endsAlnum) return `(?<!\\w)${escaped}\\b`;
      return `(?<!\\w)${escaped}(?!\\w)`;
    });
    const regex = new RegExp(`(${parts_arr.join("|")})`, "gi");
    const textParts = text.split(regex);
    return textParts.map((part, i) => regex.test(part) ? <strong key={i} className="keyword-highlight">{part}</strong> : part);
  };

  const downloadUrl = `${API}/download/${r.id}${boldOn ? "?highlight_keywords=true" : ""}`;

  return (
    <div className="fade-in" data-testid="history-detail">
      <div className="results-header">
        <div className="results-header-left">
          <h2>Optimization Detail</h2>
          <p>
            {r.original_filename}
            {r.job_title ? ` \u2192 ${r.job_title}` : ""}
            {r.company_name ? ` at ${r.company_name}` : ""}
          </p>
        </div>
        <div className="results-actions">
          <button className="btn-secondary" onClick={onBack} data-testid="back-to-history-btn">
            <ArrowLeft size={16} /> Back to History
          </button>
          <a href={downloadUrl} className="btn-download" data-testid="download-detail-btn">
            <Download size={16} /> Download
          </a>
        </div>
      </div>

      <div className="score-section">
        <div className="score-ring-wrapper">
          <div className="score-ring" data-testid="detail-score-ring">
            <svg width="160" height="160" viewBox="0 0 160 160">
              <circle className="score-ring-bg" cx="80" cy="80" r="68" />
              <circle className="score-ring-fill" cx="80" cy="80" r="68"
                stroke={getScoreColor(overall)}
                strokeDasharray={2 * Math.PI * 68}
                strokeDashoffset={2 * Math.PI * 68 - (overall / 100) * 2 * Math.PI * 68}
              />
            </svg>
            <div className="score-ring-text">
              <span className="score-ring-number" style={{ color: getScoreColor(overall) }}>{overall}</span>
              <span className="score-ring-label">ATS Score</span>
            </div>
          </div>
        </div>
        <div className="score-grid-3">
          <div className="score-card"><div className={`score-value ${getScoreClass(score.keyword_match || 0)}`}>{score.keyword_match || 0}</div><div className="score-label">Keywords</div></div>
          <div className="score-card"><div className={`score-value ${getScoreClass(score.skills_match || 0)}`}>{score.skills_match || 0}</div><div className="score-label">Skills</div></div>
          <div className="score-card"><div className={`score-value ${getScoreClass(score.experience_match || 0)}`}>{score.experience_match || 0}</div><div className="score-label">Experience</div></div>
        </div>
      </div>

      {score.summary && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", lineHeight: 1.6, margin: 0 }}>{score.summary}</p>
        </div>
      )}

      {r.cost_info?.score_explanation && (
        <div className="card" style={{ marginBottom: "1.5rem", padding: "0.8rem 1.2rem", borderLeft: "3px solid var(--accent)" }} data-testid="history-score-explanation">
          <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", lineHeight: 1.5, margin: 0 }}>{r.cost_info.score_explanation}</p>
        </div>
      )}

      {r.cost_info?.keyword_placement_plan?.length > 0 && (
        <details className="card" style={{ marginBottom: "1.5rem", padding: "1rem 1.2rem" }} data-testid="history-keyword-placement">
          <summary style={{ fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", marginBottom: "0.5rem" }}>
            Keyword Placement Report ({r.cost_info.keyword_placement_plan.length} keywords)
          </summary>
          <div style={{ overflowX: "auto", marginTop: "0.5rem" }}>
            <table style={{ width: "100%", fontSize: "0.8rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px", color: "var(--text-secondary)" }}>Keyword</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", color: "var(--text-secondary)" }}>Type</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", color: "var(--text-secondary)" }}>Where Placed</th>
                </tr>
              </thead>
              <tbody>
                {r.cost_info.keyword_placement_plan.map((kp, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border-color)", opacity: kp.type === "ignored" ? 0.5 : 1 }}>
                    <td style={{ padding: "4px 8px", fontWeight: 500 }}>{kp.keyword}</td>
                    <td style={{ padding: "4px 8px" }}>
                      <span style={{
                        fontSize: "0.7rem", padding: "1px 6px", borderRadius: "8px",
                        background: kp.type === "technical" ? "rgba(59,130,246,0.15)" : kp.type === "tool" ? "rgba(168,85,247,0.15)" : kp.type === "soft_skill" ? "rgba(245,158,11,0.15)" : kp.type === "domain" ? "rgba(16,185,129,0.15)" : "rgba(100,100,100,0.15)",
                        color: kp.type === "technical" ? "#3B82F6" : kp.type === "tool" ? "#A855F7" : kp.type === "soft_skill" ? "#F59E0B" : kp.type === "domain" ? "#10B981" : "#888",
                      }}>{kp.type}</span>
                    </td>
                    <td style={{ padding: "4px 8px", color: kp.placement === "Not Added" ? "var(--text-muted)" : "var(--text-secondary)" }}>{kp.placement}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {r.cost_info?.length_comparison && (
        <div className="card" style={{ marginBottom: "1.5rem", padding: "0.8rem 1.2rem", display: "flex", alignItems: "center", justifyContent: "space-between" }} data-testid="history-length-comparison">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem" }}>
            <span style={{ color: "var(--text-secondary)" }}>Length Change:</span>
            <span style={{
              fontWeight: 600, padding: "2px 8px", borderRadius: "6px",
              background: Math.abs(r.cost_info.length_comparison.change_pct) <= 15 ? "rgba(16,185,129,0.15)" : "rgba(245,158,11,0.15)",
              color: Math.abs(r.cost_info.length_comparison.change_pct) <= 15 ? "#10B981" : "#F59E0B",
            }}>{r.cost_info.length_comparison.change_pct > 0 ? "+" : ""}{r.cost_info.length_comparison.change_pct}%</span>
          </div>
          <div style={{ display: "flex", gap: "2rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
            <span>Original: <strong style={{ color: "var(--text-primary)" }}>{r.cost_info.length_comparison.original_words}</strong> words</span>
            <span>Optimized: <strong style={{ color: "var(--text-primary)" }}>{r.cost_info.length_comparison.optimized_words}</strong> words</span>
          </div>
        </div>
      )}

      <div className="comparison-controls">
        <label className="highlight-toggle" data-testid="highlight-toggle">
          <span className="highlight-toggle-label">{boldOn ? <Eye size={14} /> : <EyeOff size={14} />} Bold Keywords in Download</span>
          <div className={`toggle-switch ${boldOn ? "on" : ""}`} onClick={handleToggleBold} data-testid="highlight-toggle-switch"><div className="toggle-thumb" /></div>
        </label>
      </div>

      <div className="comparison-grid">
        <div className="comparison-panel">
          <div className="comparison-panel-header"><span className="comparison-panel-title">Original</span><span className="comparison-panel-badge badge-original">Before</span></div>
          <div className="comparison-panel-content" data-testid="detail-original-text">{r.original_text}</div>
        </div>
        <div className="comparison-panel">
          <div className="comparison-panel-header"><span className="comparison-panel-title">Optimized</span><span className="comparison-panel-badge badge-optimized">After</span></div>
          <div className="comparison-panel-content" data-testid="detail-optimized-text">{boldOn ? highlightText(r.optimized_text, matchedKeywords) : r.optimized_text}</div>
        </div>
      </div>
    </div>
  );
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingResult, setViewingResult] = useState(null);

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/history`);
      setHistory(res.data);
    } catch { toast.error("Failed to load history"); } finally { setLoading(false); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/history/${id}`);
      setHistory((prev) => prev.filter((h) => h.id !== id));
      toast.success("Record deleted");
    } catch { toast.error("Failed to delete record"); }
  };

  const handleView = async (id) => {
    try {
      const res = await axios.get(`${API}/history/${id}`);
      setViewingResult(res.data);
    } catch { toast.error("Failed to load optimization details"); }
  };

  const getScoreClass = (val) => val >= 75 ? "high" : val >= 50 ? "medium" : "low";

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "4rem" }}><div className="processing-spinner" /></div>;
  }

  // If viewing a result detail, show a simplified view
  if (viewingResult) {
    return <HistoryDetailView result={viewingResult} onBack={() => setViewingResult(null)} />;
  }

  return (
    <div className="fade-in" data-testid="history-page">
      <div className="history-header">
        <h2>Past Optimizations</h2>
        <p>Review and download your previously optimized resumes.</p>
      </div>

      {history.length === 0 ? (
        <div className="history-empty">
          <div className="history-empty-icon"><Clock size={24} /></div>
          <h3 style={{ margin: "0 0 0.5rem", fontFamily: "Outfit, sans-serif" }}>No optimizations yet</h3>
          <p style={{ marginBottom: "1.5rem" }}>Start by uploading your resume and a job description.</p>
          <button className="btn-primary" onClick={() => navigate("/app/optimize")} style={{ width: "auto" }} data-testid="go-optimize-btn">
            <Sparkles size={16} /> Start Optimizing
          </button>
        </div>
      ) : (
        <div className="history-grid" data-testid="history-grid">
          {history.map((item) => (
            <div key={item.id} className="history-card" onClick={() => handleView(item.id)} data-testid={`history-card-${item.id}`}>
              <div className="history-card-top">
                <div>
                  <h4 className="history-card-title">{item.job_title || "Untitled Optimization"}</h4>
                  {item.company_name && <div className="history-card-company">{item.company_name}</div>}
                </div>
                <span className={`history-score-badge ${getScoreClass(item.ats_score?.overall_score || 0)}`}>
                  {item.ats_score?.overall_score || 0}%
                </span>
              </div>
              <div className="history-card-meta">
                <span className="history-card-file"><FileText size={12} />{item.original_filename}</span>
                <span className="history-card-date">{formatDate(item.created_at)}</span>
              </div>
              <div className="history-card-actions">
                <a href={`${API}/download/${item.id}`} className="btn-icon" onClick={(e) => e.stopPropagation()} data-testid={`download-btn-${item.id}`} aria-label="Download">
                  <Download size={14} />
                </a>
                <button className="btn-icon danger" onClick={(e) => handleDelete(e, item.id)} data-testid={`delete-btn-${item.id}`} aria-label="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
